import { Fragment, type CSSProperties } from "react";
import { useTheme } from "@repo/tokens";
import { Icon } from "@repo/ui";
import { MathJaxHtml } from "../../../../../../components/mathJax/MathJaxHtml";
import type { QuestionData } from "../../models/GetQuestions";
import {
  getMatrixDataCellStyle,
  MATRIX_SELECT_ICON_HEIGHT,
  MATRIX_SELECT_ICON_WIDTH,
} from "./matrixTwoColumnCellStyle";
import styles from "./MatrixTwoColumnScreen.module.scss";

const parseAnswer = (val: string): Record<number, number> => {
  try {
    return val ? JSON.parse(val) : {};
  } catch {
    return {};
  }
};

const serializeAnswer = (map: Record<number, number>): string =>
  JSON.stringify(map);

const numberToRoman = (n: number): string => {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = [
    "M",
    "CM",
    "D",
    "CD",
    "C",
    "XC",
    "L",
    "XL",
    "X",
    "IX",
    "V",
    "IV",
    "I",
  ];
  let rem = n;
  let result = "";
  for (let i = 0; i < vals.length; i++) {
    while (rem >= vals[i]) {
      result += syms[i];
      rem -= vals[i];
    }
  }
  return result;
};

type Props = {
  questionData: QuestionData;
  selectedValue: string;
  setSelectedValue: (val: string) => void;
  onDone?: () => void;
};

export const MatrixTwoColumnScreen = ({
  questionData,
  selectedValue,
  setSelectedValue,
  onDone,
}: Props) => {
  const { theme } = useTheme();
  const isAttempted = questionData.isAttempted ?? false;
  const answerOptions = questionData.question?.answer_options ?? [];

  const col1Items: string[] = answerOptions[0] ?? [];
  const col2Items: string[] = answerOptions[1] ?? [];

  const rowLabels = col1Items.map((_, i) => String(i + 1));
  const col2ListLabels = col2Items.map((_, i) =>
    numberToRoman(i + 1).toLowerCase(),
  );
  const matrixRowLabels = col1Items.map((_, i) =>
    String.fromCharCode("A".charCodeAt(0) + i),
  );
  const matrixColLabels = col2Items.map((_, i) =>
    String.fromCharCode("P".charCodeAt(0) + i),
  );

  const correctMap: Record<number, number> = {};
  col1Items.forEach((_, i) => {
    const c = questionData.question?.correct_answer?.[i]?.[0];
    if (c != null) correctMap[i] = Number(c) - 1;
  });

  const userMap = parseAnswer(selectedValue);

  const handleCellPress = (rowIdx: number, colIdx: number) => {
    if (isAttempted) return;
    const current = parseAnswer(selectedValue);
    if (current[rowIdx] === colIdx) {
      delete current[rowIdx];
    } else {
      current[rowIdx] = colIdx;
    }
    const hasAnswers = Object.keys(current).length > 0;
    setSelectedValue(hasAnswers ? serializeAnswer(current) : "");
  };

  return (
    <div className={styles.root}>
      <div className={styles.contentLayout}>
        <div
          className={styles.textColumnsWrap}
          style={{ "--rq-col-divider": theme.border.default } as CSSProperties}
        >
          <div className={styles.twoColRow}>
            <div
              className={`${styles.colHeader} ${styles.col1Band}`}
              style={{
                gridColumn: 1,
                gridRow: 1,
                color: theme.colors.neutral[70],
              }}
            >
              Column I
            </div>
            <div
              className={`${styles.colHeader} ${styles.col2Band}`}
              style={{
                gridColumn: 2,
                gridRow: 1,
                color: theme.colors.neutral[70],
              }}
            >
              Column II
            </div>
            {col1Items.map((item, i) => (
              <Fragment key={i}>
                <div
                  className={`${styles.optionRow} ${styles.col1Band}`}
                  style={{ gridColumn: 1, gridRow: i + 2 }}
                >
                  <span
                    className={styles.optionLabel}
                    style={{ color: theme.text.secondary }}
                  >
                    ({rowLabels[i]})
                  </span>
                  <div
                    className={`${styles.optionTextFlex} ${styles.optionTextCol1}`}
                  >
                    <MathJaxHtml
                      content={item}
                      className={styles.reviseTextMathStyle}
                    />
                  </div>
                </div>
                <div
                  className={`${styles.optionRow} ${styles.col2Band}`}
                  style={{ gridColumn: 2, gridRow: i + 2 }}
                >
                  <span
                    className={styles.optionLabel}
                    style={{ color: theme.text.secondary }}
                  >
                    ({col2ListLabels[i] ?? numberToRoman(i + 1).toLowerCase()})
                  </span>
                  <div className={styles.optionTextFlex}>
                    <MathJaxHtml
                      content={col2Items[i] ?? ""}
                      className={styles.reviseTextMathStyle}
                    />
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
        </div>

        <div className={styles.matrixWrap}>
          <div
            className={styles.matrixOuter}
            style={
              {
                "--rq-matrix-border": theme.border.default,
                "--rq-matrix-header-bg": theme.colors.neutral[100],
              } as CSSProperties
            }
          >
            <div className={styles.matrixRow}>
              <div className={styles.cornerCell} />
              {matrixColLabels.map((label, ci) => (
                <div key={ci} className={styles.headerCell}>
                  <span
                    className={styles.headerCellText}
                    style={{ color: theme.text.secondary }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {matrixRowLabels.map((rowLabel, ri) => (
              <div key={ri} className={styles.matrixRow}>
                <div className={styles.rowLabelCell}>
                  <span
                    className={styles.rowLabelText}
                    style={{ color: theme.text.secondary }}
                  >
                    {rowLabel}
                  </span>
                </div>

                {matrixColLabels.map((_, ci) => {
                  const cellStyle = getMatrixDataCellStyle(
                    theme,
                    isAttempted,
                    userMap,
                    correctMap,
                    ri,
                    ci,
                  );
                  const isUserSelected = userMap[ri] === ci;
                  const isAttemptedCorrect =
                    isAttempted && correctMap[ri] === ci;
                  const isAttemptedWrong =
                    isAttempted && isUserSelected && correctMap[ri] !== ci;
                  const showSelectIcon =
                    isAttemptedCorrect ||
                    isAttemptedWrong ||
                    (isUserSelected && !isAttempted);

                  return (
                    <button
                      key={ci}
                      type="button"
                      disabled={isAttempted}
                      className={styles.dataCell}
                      style={{ backgroundColor: cellStyle.backgroundColor }}
                      onClick={() => handleCellPress(ri, ci)}
                    >
                      {showSelectIcon ? (
                        <Icon
                          name="selectIcon"
                          width={MATRIX_SELECT_ICON_WIDTH}
                          height={MATRIX_SELECT_ICON_HEIGHT}
                          color={cellStyle.textColor}
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {!isAttempted && Object.keys(userMap).length > 0 ? (
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.linkText}
                style={{ color: theme.text.interactive }}
                onClick={() => setSelectedValue("")}
              >
                Clear
              </button>
              <button
                type="button"
                className={styles.linkText}
                style={{ color: theme.text.interactive }}
                onClick={() => onDone?.()}
              >
                Done
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
